"use client"

import * as React from "react"
import { X as CloseIcon, Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

export type Option = {
  value: string
  label: string
}

type MultiSelectProps = {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Wählen Sie Optionen aus...",
  className,
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)

  const handleUnselect = (value: string) => {
    if (disabled) return;
    console.log("MultiSelect: Entferne Auswahl:", value);
    onChange(selected.filter((s) => s !== value))
  }

  const handleSelect = (value: string) => {
    if (disabled) return;
    console.log("MultiSelect: Änderung für Wert:", value, "Current selected:", selected);
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled ? "cursor-not-allowed opacity-50 bg-muted" : "cursor-pointer"
        )}
        onClick={() => {
          if (!disabled) {
            console.log("MultiSelect: Toggle Open:", !open);
            setOpen(!open);
          }
        }}
      >
        <div className="flex flex-wrap gap-1">
          {selected.length === 0 && (
            <div className="text-muted-foreground py-0.5">{placeholder}</div>
          )}
          {selected.map((value) => {
            const option = options.find((opt) => opt.value === value)
            if (!option) return null
            return (
              <Badge key={value} variant="secondary" className="flex items-center gap-1">
                {option.label}
                <button
                  className={cn(
                    "rounded-full outline-none focus:ring-2 ring-ring",
                    disabled ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                  disabled={disabled}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !disabled) {
                      handleUnselect(value)
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!disabled) {
                      handleUnselect(value)
                    }
                  }}
                >
                  <CloseIcon className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      </div>
      {open && !disabled && (
        <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md max-h-60 overflow-y-auto">
          <div className="p-1">
            {options.map((option) => {
              const isSelected = selected.includes(option.value)
              return (
                <div
                  key={option.value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Direct click on option:", option.value);
                    handleSelect(option.value);
                  }}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded hover:bg-accent text-sm",
                    isSelected ? "bg-accent" : ""
                  )}
                >
                  <div className={cn(
                    "border mr-2 flex h-4 w-4 items-center justify-center rounded-sm",
                    isSelected ? "bg-primary border-primary text-primary-foreground" : "opacity-50 border-muted-foreground"
                  )}>
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span>{option.label}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
} 